import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { sunTimes, type SunEvents } from '@jupi/shared'
import { formatClockTime, formatTime, useClubToday } from '@jupi/ui'

function Time({ iso }: { iso: string }) {
  return (
    <Tooltip title={`${formatClockTime(iso, 'UTC')} UTC`}>
      <span>{formatTime(iso)}</span>
    </Tooltip>
  )
}

function Range({ events }: { events: SunEvents }) {
  return (
    <>
      <Time iso={events.rise!} />–<Time iso={events.set!} />
    </>
  )
}

export function SunTimesRow({ lat, lon }: { lat: number; lon: number }) {
  const { t } = useTranslation()
  const today = useClubToday()
  const { sun, civilTwilight } = sunTimes(today, lat, lon)

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5, color: 'text.secondary' }}
    >
      {sun.state === 'normal' ? (
        <>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Icon icon="mdi:weather-sunset-up" width={20} aria-label={t('weather.sun.sunrise')} />
            <Typography variant="body2" color="text.primary">
              <Time iso={sun.rise!} />
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Icon icon="mdi:weather-sunset-down" width={20} aria-label={t('weather.sun.sunset')} />
            <Typography variant="body2" color="text.primary">
              <Time iso={sun.set!} />
            </Typography>
          </Stack>
        </>
      ) : (
        <Typography variant="body2" color="text.primary">
          {t(sun.state === 'alwaysUp' ? 'weather.sun.midnightSun' : 'weather.sun.polarNight')}
        </Typography>
      )}
      <Tooltip title={t('weather.sun.nightHint')}>
        <Typography variant="body2">
          {civilTwilight.state === 'normal' ? (
            <>
              {t('weather.sun.civilTwilight')} <Range events={civilTwilight} />
            </>
          ) : civilTwilight.state === 'alwaysUp' ? (
            t('weather.sun.noNight')
          ) : (
            t('weather.sun.nightAllDay')
          )}
        </Typography>
      </Tooltip>
      <Typography variant="caption">{t('weather.sun.localTime')}</Typography>
    </Stack>
  )
}
